import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthenticationService } from './auth/authentication.service';
import { PermissionsMap, ResolvedPermission } from '../auth/auth.models';
import { environment } from 'src/environments/environment';

const BASE_URL = environment.API_URL;

function getHeaders(): HttpHeaders {
  const token = localStorage.getItem('currentUser')
    ? JSON.parse(localStorage.getItem('currentUser')).token
    : '';
  const normalized = typeof token === 'string'
    ? token.replace(/^Bearer\s+/i, '').trim()
    : '';
  return normalized
    ? new HttpHeaders({ Authorization: normalized })
    : new HttpHeaders();
}

@Injectable({ providedIn: 'root' })
export class PermissionsService {

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService
  ) {}

  // ── Helpers de acceso rápido ──────────────────────────────────────────────

  /** Devuelve el mapa completo de permisos del usuario logueado */
  get permissions(): PermissionsMap {
    return this.authService.currentUserValue?.permissions || {};
  }

  /** Verifica si el usuario puede realizar una acción sobre un ítem de menú */
  can(menuItemKey: string, action: keyof ResolvedPermission = 'can_view'): boolean {
    const perm = this.permissions[menuItemKey];
    if (!perm) return false;
    return perm[action] === true;
  }

  canView(key: string):   boolean { return this.can(key, 'can_view');   }
  canCreate(key: string): boolean { return this.can(key, 'can_create'); }
  canEdit(key: string):   boolean { return this.can(key, 'can_edit');   }
  canDelete(key: string): boolean { return this.can(key, 'can_delete'); }
  canExport(key: string): boolean { return this.can(key, 'can_export'); }

  /** Devuelve el data_scope efectivo para un ítem */
  getScope(key: string): string {
    return this.permissions[key]?.data_scope || 'own';
  }

  // ── Llamadas API ──────────────────────────────────────────────────────────

  getModules(): Observable<any[]> {
    return this.http.get<any[]>(`${BASE_URL}/permissions/modules`, { headers: getHeaders() });
  }

  createModule(data: any): Observable<any> {
    return this.http.post<any>(`${BASE_URL}/permissions/modules`, data, { headers: getHeaders() });
  }

  updateModule(id: number, data: any): Observable<any> {
    return this.http.put<any>(`${BASE_URL}/permissions/modules/${id}`, data, { headers: getHeaders() });
  }

  deleteModule(id: number): Observable<any> {
    return this.http.delete<any>(`${BASE_URL}/permissions/modules/${id}`, { headers: getHeaders() });
  }

  getAllMenuItems(): Observable<any[]> {
    return this.http.get<any[]>(`${BASE_URL}/permissions/menu-items`, { headers: getHeaders() });
  }

  createMenuItem(data: any): Observable<any> {
    return this.http.post<any>(`${BASE_URL}/permissions/menu-items`, data, { headers: getHeaders() });
  }

  updateMenuItem(id: number, data: any): Observable<any> {
    return this.http.put<any>(`${BASE_URL}/permissions/menu-items/${id}`, data, { headers: getHeaders() });
  }

  deleteMenuItem(id: number): Observable<any> {
    return this.http.delete<any>(`${BASE_URL}/permissions/menu-items/${id}`, { headers: getHeaders() });
  }

  getProfiles(): Observable<any[]> {
    return this.http.get<any[]>(`${BASE_URL}/permissions/profiles`, { headers: getHeaders() });
  }

  createProfile(profile: any): Observable<any> {
    return this.http.post<any>(`${BASE_URL}/permissions/profiles`, profile, { headers: getHeaders() });
  }

  updateProfile(id: number, data: any): Observable<any> {
    return this.http.put<any>(`${BASE_URL}/permissions/profiles/${id}`, data, { headers: getHeaders() });
  }

  getProfilePermissions(profileId: number): Observable<any[]> {
    return this.http.get<any[]>(`${BASE_URL}/permissions/profiles/${profileId}/permissions`, { headers: getHeaders() });
  }

  saveProfilePermissions(profileId: number, permissions: any[]): Observable<any> {
    return this.http.post<any>(`${BASE_URL}/permissions/profiles/permissions`,
      { profile_id: profileId, permissions }, { headers: getHeaders() });
  }

  getUserProfiles(userId: string): Observable<any[]> {
    return this.http.get<any[]>(`${BASE_URL}/permissions/users/${userId}/profiles`, { headers: getHeaders() });
  }

  assignProfile(userId: string, profileId: number, area?: string): Observable<any> {
    return this.http.post<any>(`${BASE_URL}/permissions/users/assign-profile`,
      { user_id: userId, profile_id: profileId, area }, { headers: getHeaders() });
  }

  removeProfile(userId: string, profileId: number): Observable<any> {
    return this.http.delete<any>(`${BASE_URL}/permissions/users/${userId}/profiles/${profileId}`, { headers: getHeaders() });
  }

  getUserOverrides(userId: string): Observable<any[]> {
    return this.http.get<any[]>(`${BASE_URL}/permissions/users/${userId}/overrides`, { headers: getHeaders() });
  }

  saveUserOverrides(userId: string, overrides: any[]): Observable<any> {
    return this.http.post<any>(`${BASE_URL}/permissions/users/overrides`,
      { user_id: userId, overrides }, { headers: getHeaders() });
  }

  getEffectivePermissions(userId: string): Observable<PermissionsMap> {
    return this.http.get<PermissionsMap>(`${BASE_URL}/permissions/users/${userId}/effective`, { headers: getHeaders() });
  }

  getMyEffectivePermissions(): Observable<PermissionsMap> {
    return this.http.get<PermissionsMap>(`${BASE_URL}/permissions/me/effective`, { headers: getHeaders() });
  }
}
